import {
  React,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Row,
} from 'react-bootstrap';

import { TextControl } from '../controls/text.jsx';
import {
  prepareDocuments,
  saveSession,
  sendEmailVerification,
} from '../libs/api.js';
import {
  asPhoneNumber,
  isEmail,
} from '../libs/contact.js';
import {
  LayoutContext,
  SessionContext,
} from '../libs/contexts.js';

export const EmailValidationBox = ({ setEmailOk }) => {

    const { layout, setLayout } = useContext(LayoutContext);
    const { session, setSession } = useContext(SessionContext);
    const [optout, setOptout] = useState(false);
    const [editing, setEditing] = useState(false);
    const [code, setCode] = useState("");
    const [allowResend, setAllowResend] = useState(true);
    const [message, setMessage] = useState(false);
    const [currEmailAddress, setCurrEmailAddress] = useState("");
    const [spinner, setSpinner] = useState(false);
    const [valid, setValid] = useState(false);
    const [prevEmailAddress, setPrevEmailAddress] = useState("");

    useEffect(() => {
        setCurrEmailAddress(session.email || "");
        setPrevEmailAddress(session.email || "");
        sendCode({ isResend: false });
    }, []);

    useEffect(() => {
        if (isEmail({ contactInfo: session.email })) {
            setAllowResend(true);
        } else {
            setAllowResend(false);
        }
    }, [session.email]);

    const handleChange = (e) => {
        setMessage(false);
        let value = e.target.value;
        if ("formCode" === e.target.id) {
            const maxDigits = 6;
            value = value.replace(/[^0-9\-]/gi, '').substr(0, maxDigits);
            setCode(value);
            setValid(maxDigits === value.length);
        }
    }

    const onEdit = ({ id, value }) => {
        if ("phone" === id) {
            if (value && value.length > 3) {
                value = asPhoneNumber({ contactInfo: value });
                setSession({
                    phone: value,
                });
            }
        } else if ("email" === id) {
            setCurrEmailAddress(value);
        }
    }

    const sendCode = ({ isResend }) => {
        setAllowResend(false);
        setSpinner(true);

        sendEmailVerification({
            quote: session.quote,
            email: session.email,
            token: session.token,
            next: (data) => {
                setSpinner(false);
                if (isResend === true) {
                    setMessage("Please check your inbox. A new code should arrive within a minute or two.");
                }
                if (data.code && data.email) {
                    let newSessionEntry = {
                        verification_code: data.code,
                        email: data.email
                    }
                    setSession(newSessionEntry);
                }
            }
        });

        setTimeout(() => {
            setAllowResend(true);
            setMessage(false);
        }, 8000);
    }

    const verifyCode = () => {
        if (valid && session.verification_code && session.verification_code === code) {
            setEmailOk(true);
            setSession({
                email_verified : "yes"
            });
        } else {
            setMessage("This code is not valid. Please check your email and/or request a new code.");
        }
    }

    const updateEmail = () => {
        setEditing(false); //do this no matter what
        if (isEmail({ contactInfo: currEmailAddress })) {
            if (prevEmailAddress !== currEmailAddress) {
                setPrevEmailAddress(currEmailAddress);
                setSession({ email: currEmailAddress });
                saveSession({ quote: session, token: session.token, next: (data) => {
                    prepareDocuments({ quote: session, token: session.token, next: (data) => {} });
                    sendCode({ isResend: true });
                }});
            } //else do nothing
        }
    }

    return (
        <Container fluid>
            <Row>
                <Col>
                    {editing ?
                        <h5>Update your contact information</h5>
                        :
                        <h5>Email verification</h5>
                    }
                </Col>
            </Row>
            {optout ?
                <>
                    <Row>
                        <Col>
                            <p>
                                An email verification code is a great way to confirm that your contact information is correct, but it is not mandatory.
                            </p>
                            <p>
                                Can you confirm that the following contact information is correct?
                            </p>
                            <p>Phone: {session.phone}</p>
                            <p>Email: {session.email}</p>

                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button onClick={() => setEmailOk(true)} variant="outline-primary">Confirm</Button>{' '}
                        </Col>
                    </Row>
                    <Row className="mt-4">
                        <Col>
                            <small onClick={() => setOptout(false)} className="clicker" style={{ textDecoration: "underline" }}>Back to email verification</small>
                        </Col>
                    </Row>
                </>
                :
                <>
                    {editing ?
                        <>
                            <Row>
                                <Col>
                                    <TextControl label="Phone number" title="Please provide us with the best number to contact you" field="phone" placeholder="Phone number" onChange={onEdit} required={true} type="phone" />
                                </Col>
                            </Row>
                            <Row className="mt-2">
                                <Col>
                                    <TextControl label="Email address" title="Please provide us with the best email address to contact you" field="email" placeholder="Email address" onChange={onEdit} required={true} type="email" />
                                </Col>
                            </Row>
                            <Row className="mt-2">
                                <Col>
                                    <Button onClick={updateEmail} variant="outline-primary">Confirm</Button>
                                </Col>
                            </Row>
                        </>
                        :
                        <>
                            <Row>
                                <Col>
                                    <p>
                                        We sent a 6-digit code to your email address. Please enter this code below.
                                    </p>
                                    <Form>
                                        <Form.Group className="mb-3" controlId="formEmail">
                                            <Form.Label>Email address</Form.Label>
                                            <Form.Control type="email" readOnly={true} disabled={true} value={currEmailAddress}/>
                                                <Form.Text className="text-muted">
                                                    This is the email address you provided. <u className='clicker' onClick={() => setEditing(true)}>Change</u>
                                                </Form.Text>
                                        </Form.Group>

                                        <Form.Group className="mb-3" controlId="formCode">
                                            <Form.Label>6-digit code</Form.Label>
                                            <Form.Control type="text" placeholder="Enter 6-digit code" value={code} onChange={handleChange} />
                                        </Form.Group>
                                        <Button variant="outline-primary" readOnly={!valid} disabled={!valid} type="button" onClick={verifyCode}>
                                            Confirm
                                        </Button>{' '}
                                        {allowResend &&
                                            <Button variant="outline-primary" type="button" onClick={() => sendCode({ isResend: true })}>
                                                Resend code
                                            </Button>}
                                    </Form>
                                </Col>
                            </Row>
                            <Row className="mt-4">
                                <Col>
                                    <small onClick={() => setOptout(true)} className="clicker" style={{ textDecoration: "underline" }}>Skip email verification</small>
                                </Col>
                            </Row>
                            {spinner &&
                                <Row className='mt-4'>
                                    <Col>
                                        <div className='dot-pulse'></div>
                                    </Col>
                                </Row>
                            }
                            {message &&
                                <Row className='mt-4'>
                                    <Col>
                                        <Alert variant="info">
                                            {message}
                                        </Alert>
                                    </Col>
                                </Row>
                            }
                        </>
                    }
                </>
        }
        </Container>
    );
}
